import { Box, styled } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';

import { ResourceRegionType } from '#shared';

import { useGeoMap } from '../../GeoMap';
import { subRegionMap, surgeonLabelsMap } from '../../helpers';
import { SurgeonGroupCard } from '../../HubStatusCard';
import { surgeonGroupAnchors } from '../lib';

import type { StaffInfo } from '#entities';

const Root = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
});
const Floating = styled(Box)({
  position: 'absolute',
  transform: 'translate(-50%, -100%)',
  pointerEvents: 'auto',
});

type RegionedSurgeons = Record<ResourceRegionType, StaffInfo[][]>;

interface Props {
  regionedSurgeons: RegionedSurgeons;
  onClickSurgeon: (regionName: string) => void;
}

export const SurgeonsAtCoordinates = ({
  regionedSurgeons,
  onClickSurgeon,
}: Props) => {
  const { projectionRef } = useGeoMap();

  const project = useCallback(
    (lonLat: [number, number], dx = 0, dy = 0) => {
      const p = projectionRef.current;
      const [x, y] = p(lonLat) || [NaN, NaN];
      return {
        left: `${Math.round(x + dx)}px`,
        top: `${Math.round(y + dy)}px`,
      };
    },
    [projectionRef],
  );

  const nodes = useMemo(() => {
    const out: ReactNode[] = [];

    (Object.values(ResourceRegionType) as ResourceRegionType[]).forEach(
      region => {
        const groups = regionedSurgeons[region] ?? [];
        const anchors = surgeonGroupAnchors[region] ?? [];
        const labels = surgeonLabelsMap[region] ?? [];
        const subNames = subRegionMap[region] ?? [];

        const n = Math.min(
          groups.length,
          anchors.length,
          labels.length || Infinity,
        );
        for (let i = 0; i < n; i++) {
          const staff = groups[i];
          if (!staff?.length) {
            continue;
          }

          const a = anchors[i];
          const style = project(a.lonLat, a.dx ?? 0, a.dy ?? 0);
          const isEastOverride = a.alignment === 'right';
          const label = labels[i];
          const regionName = subNames[i] ?? subNames[0];

          out.push(
            <Floating key={`${region}-${i}`} style={style}>
              <SurgeonGroupCard
                surgeons={staff}
                region={region}
                label={label}
                isEastOverride={isEastOverride}
                onClick={() => onClickSurgeon(regionName)}
              />
            </Floating>,
          );
        }
      },
    );

    return out;
  }, [regionedSurgeons, project, onClickSurgeon]);

  return <Root>{nodes}</Root>;
};
